'use client'
import { useEffect, useState } from 'react'
import chartSnapshot from './manufacturer-size-chart.json'
import { SHIRT_SIZE_SOURCE } from './apparel'

// Same public chart feed and original diagram used by the manufacturer's
// product-page popup. The last fetched chart is kept as an offline fallback.
const clean = (value: string) => value.replace(/&nbsp;|\u00a0/g, ' ').trim()
const inCentimeters = (value: string) => {
  const [whole, fraction] = value.split(/\s+/)
  const [numerator, denominator] = (fraction ?? '0/1').split('/').map(Number)
  return Math.ceil((Number(whole) + numerator / denominator) * 2.54)
}
export default function ManufacturerSizeChart() {
  const [chart, setChart] = useState(chartSnapshot)
  const [unit, setUnit] = useState<'in' | 'cm'>('in')
  useEffect(() => {
    const controller = new AbortController()
    void fetch(chartSnapshot.source, { signal: controller.signal }).then(async response => {
      if (!response.ok) return
      const [product] = await response.json()
      const inches = product?.acf?.inches
      if (!Array.isArray(inches?.header) || !Array.isArray(inches?.body)) return
      const headers = inches.header.map((cell: { c: string }) => clean(cell.c))
      const rows = inches.body.map((row: { c: string }[]) => row.map(cell => clean(cell.c)))
      if (headers.length !== 4 || !rows.length || rows.some((row: string[]) => row.length !== 4)) return
      const image = new URL(product.better_featured_image.source_url)
      if (image.protocol !== 'https:' || image.hostname !== 'laapparel.info') return
      setChart({ ...chartSnapshot, headers, rows, image: image.href })
    }).catch(() => { /* Keep the manufacturer's last verified chart offline. */ })
    return () => controller.abort()
  }, [])
  return <div className="manufacturer-size-chart">
    <div className="manufacturer-units" aria-label="Measurement units">
      <button aria-pressed={unit === 'in'} onClick={() => setUnit('in')}>IN</button>
      <button aria-pressed={unit === 'cm'} onClick={() => setUnit('cm')}>CM</button>
    </div>
    <table>
      <caption className="sr-only">Los Angeles Apparel 2017 measurements in {unit === 'in' ? 'inches' : 'centimeters'}</caption>
      <thead><tr>{chart.headers.map((header, index) => <th scope="col" key={index}>{header}</th>)}</tr></thead>
      <tbody>{chart.rows.map(row => <tr key={row[0]}><th scope="row">{row[0]}</th>{row.slice(1).map((value, index) => <td key={index}>{unit === 'in' ? value : inCentimeters(value)}</td>)}</tr>)}</tbody>
    </table>
    <img src={chart.image} alt="Los Angeles Apparel diagram showing how to measure body length, chest width and sleeve length" width={1625} height={1000} loading="lazy"/>
    <a href={SHIRT_SIZE_SOURCE} target="_blank" rel="noreferrer">Los Angeles Apparel · 2017 ↗</a>
  </div>
}
